import ComponentHeader from '@atoms/ComponentHeader/ComponentHeader';
import { IImageWithProductFeature } from '@models/IImageWithProductFeature';
import ImageLightBox from '@molecules/ImageLightBox/ImageLightBox';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import ProductCard from '@molecules/ProductCard/ProductCard';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';

const ImageWithProductFeature = ({
  media,
  product,
  title,
  body,
  cta,
}: IImageWithProductFeature) => {
  const isMobile = useMediaQuery(1024);
  const isImage = media && 'image' in media;
  const isProductAvailable = !!product;

  const isImagePortrait =
    isImage && media.image?.desktop
      ? media.image.desktop.height > media.image.desktop.width
      : false;
  const isComponentHeaderDisplayed =
    !isImagePortrait && (title || body || cta?.url);
  const [imageDescriptionRef, { height: imageDescriptionHeight }] =
    useResizeObserver();

  return (
    <section
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-78-Image-With-Product-Feature"
    >
      <SoftLoadWrapper>
        {isComponentHeaderDisplayed && (
          <ComponentHeader
            cta={cta}
            title={title}
            subtitle={body}
            hasTextMaxWidth
            dataTestId="CT-78-Image-With-Product-Feature-Header"
          />
        )}

        <div
          className={classNames(
            'container grid grid-cols-1 gap-x-4 lg:grid-cols-12',
            { 'mt-10': isComponentHeaderDisplayed }
          )}
        >
          <div
            className={classNames(
              'flex flex-col items-center justify-center lg:mb-0',
              {
                'mb-8': isProductAvailable,
                'lg:col-span-5 lg:col-start-2': isImagePortrait,
                'lg:col-span-7 xl:col-span-8':
                  !isImagePortrait && isProductAvailable,
                'lg:col-span-12': !isImagePortrait && !isProductAvailable,
              }
            )}
          >
            {isImage ? (
              <div className="relative w-full">
                <ImageLightBox
                  {...media}
                  contentIds={
                    media.meta?.contentLink
                      ? [media.meta.contentLink as string]
                      : []
                  }
                  imageClasses="w-full h-full"
                  dataTestId="CT-78-Image-With-Product-Feature-Image"
                />
                {media.description && (
                  <div
                    ref={imageDescriptionRef}
                    className="body-four pt-4 lg:mb-0 dark:text-white"
                    dangerouslySetInnerHTML={{ __html: media.description }}
                    data-testid="CT-78-Image-With-Product-Feature-Description"
                  />
                )}
              </div>
            ) : (
              <InViewVideo
                video={media}
                hasInVideoPlayButton
                controls={false}
                autoPlay
                wrapperClasses="relative h-full w-full"
                videoClasses="h-full w-full [&>div>video]:object-cover"
                dataTestId="CT-78-Image-With-Product-Feature-Video"
              />
            )}
          </div>

          {isProductAvailable && (
            <div
              style={{
                paddingBottom:
                  isMobile || !imageDescriptionHeight
                    ? 0
                    : imageDescriptionHeight + 16,
              }}
              className={classNames(
                'flex items-center justify-center lg:col-span-5 xl:col-span-4',
                isImagePortrait && 'lg:col-start-8 xl:col-start-8'
              )}
            >
              <div
                className={classNames(
                  isImagePortrait ? 'min-h-[69%]' : 'h-full',
                  'flex w-full'
                )}
              >
                <ProductCard
                  classes={isImagePortrait ? 'h-auto' : ''}
                  tabletImageContainerHeight="md:h-[254px]"
                  {...product}
                  dataTestId="CT-78-Image-With-Product-Feature-Product"
                />
              </div>
            </div>
          )}
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default ImageWithProductFeature;
